/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {CreateJob} from '@ion/core/src/types/entities';
import {liveJobsEnabled} from './signals/liveUpdates';
import {getWatchCreatedJobsDefault, watch} from './WatchedJobs';

/**
 * Applies the stored create-job default to a job a rerun created.
 *
 * The Create Job dialog's counterpart, `useAutoWatchCreatedJob`, reads the
 * `notifyOnCreatedJob` signal instead, and the difference is the point: that
 * signal holds the answer the user gave in the dialog, and a rerun has no
 * dialog to give one in. Reading the signal here would carry the last dialog's
 * answer - or the module default - into a job it was never about, so the stored
 * preference is asked directly.
 *
 * Registered against `onJobRerun` at sign-in, after `hydrate()` has run, which
 * is what makes `getWatchCreatedJobsDefault` meaningful by the time a rerun can
 * reach it. Gated on the feature flag for the same reason every other watch
 * surface is: with the flag off there is no panel to notify and no bell to
 * show, so a silently growing watch list is the only thing it could achieve.
 *
 * The write is deliberately not awaited. Nothing the rerun does next depends on
 * it, `watch` is a no-op on an already-watched id, and a failed preference save
 * logs on its own inside `WatchedJobs`.
 *
 * ```typescript
 *
 *   import {watchRerunJob} from '.../watchRerunJob';
 *
 *   ...
 *   disposeRerunListener = onJobRerun(watchRerunJob);
 *
 * ```
 *
 * @param job The job the rerun created, as the service answered with it.
 */
export const watchRerunJob = (job: CreateJob): void => {
  const jobId = Number(job?.id);

  if (liveJobsEnabled.value && jobId && !isNaN(jobId) && getWatchCreatedJobsDefault()) {
    watch(jobId);
  }
};

export default watchRerunJob;
